import { addYmd, kyivWeekdayIndex, ymdToUtcNoon } from "@/lib/kyiv";
import type { BellDto, DayLessonDto, DayTimetable, LessonDto, LessonNow } from "@/lib/types";

export const WEEKDAY_SHORT = ["Нд", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"] as const;
export const WEEKDAY_LONG_UK = ["неділя", "понеділок", "вівторок", "середа", "четвер", "пʼятниця", "субота"] as const;

export const SUBJECT_CHIPS = [
  "ЯДС",
  "Українська мова",
  "Англійська мова",
  "Читання",
  "Математика",
  "Фізкультура",
  "Інформатика",
  "Музика",
  "Малювання",
  "Малювання / музика",
  "Християнська етика",
  "Трудове навчання",
] as const;

export const DEFAULT_BELLS: BellDto[] = [
  { sortOrder: 1, start: "08:30", end: "09:15" },
  { sortOrder: 2, start: "09:25", end: "10:10" },
  { sortOrder: 3, start: "10:20", end: "11:05" },
  { sortOrder: 4, start: "11:25", end: "12:10" },
  { sortOrder: 5, start: "12:30", end: "13:15" },
];

/** Розклад 2-Б зі зошита класного. */
export const DEFAULT_WEEK: Record<number, string[]> = {
  1: ["ЯДС", "Українська мова", "Англійська мова", "Фізкультура", "Математика"],
  2: ["ЯДС", "Українська мова", "Читання", "Математика", "Фізкультура"],
  3: ["Англійська мова", "Українська мова", "Читання", "Математика", "Малювання / музика"],
  4: ["ЯДС", "Читання", "Українська мова", "Інформатика", "Християнська етика"],
  5: ["ЯДС", "Математика", "Фізкультура", "Англійська мова", "Трудове навчання"],
};

const SHORT_SUBJECT: Record<string, string> = {
  ЯДС: "ЯДС",
  "Українська мова": "Укр. мова",
  "Англійська мова": "Англ. мова",
  Фізкультура: "Фіз. культ.",
  Математика: "Матем.",
  Читання: "Читання",
  "Малювання / музика": "Мал. / муз.",
  Малювання: "Мал.",
  Інформатика: "Інформ.",
  "Християнська етика": "Христ. ет.",
  Музика: "Музика",
  "Трудове навчання": "Труд. навч.",
};

export function shortSubject(title: string): string {
  if (SHORT_SUBJECT[title]) return SHORT_SUBJECT[title];
  return title.length > 8 ? `${title.slice(0, 7).trimEnd()}.` : title;
}

export function compactHhmm(hhmm: string): string {
  const [hours, mins] = hhmm.split(":");
  if (!hours || !mins) return hhmm;
  return `${Number(hours)}:${mins}`;
}

export function weekdayIndexYmd(ymd: string): number {
  return kyivWeekdayIndex(ymdToUtcNoon(ymd));
}

export function weekdayLabel(weekday: number): string {
  return WEEKDAY_LONG_UK[weekday] ?? "";
}

function capitalizeUk(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function hhmmToMinutes(value: string): number {
  const [hours, mins] = value.split(":").map(Number);
  return hours * 60 + mins;
}

export function lessonsForWeekday(lessons: LessonDto[], bells: BellDto[], weekday: number): DayLessonDto[] {
  const bySlot = new Map(lessons.filter((lesson) => lesson.weekday === weekday).map((lesson) => [lesson.slotOrder, lesson.title]));
  return bells
    .filter((bell) => bySlot.has(bell.sortOrder))
    .map((bell) => ({
      slotOrder: bell.sortOrder,
      title: bySlot.get(bell.sortOrder) ?? "",
      start: bell.start,
      end: bell.end,
    }));
}

export function computeNow(dayLessons: DayLessonDto[], time: string, schoolDay: boolean): LessonNow {
  if (!schoolDay || dayLessons.length === 0) return { kind: "off" };
  const first = dayLessons[0];
  const last = dayLessons[dayLessons.length - 1];
  if (time < first.start) {
    return { kind: "before", slotOrder: first.slotOrder, title: first.title, start: first.start };
  }
  if (time >= last.end) return { kind: "done" };
  for (let i = 0; i < dayLessons.length; i += 1) {
    const lesson = dayLessons[i];
    if (time >= lesson.start && time < lesson.end) {
      return { kind: "lesson", slotOrder: lesson.slotOrder, title: lesson.title, end: lesson.end };
    }
    const next = dayLessons[i + 1];
    if (next && time >= lesson.end && time < next.start) {
      return { kind: "break", slotOrder: next.slotOrder, title: next.title, start: next.start };
    }
  }
  return { kind: "done" };
}

export function buildDayTimetable(input: {
  date: string;
  schoolDay: boolean;
  bells: BellDto[];
  lessons: LessonDto[];
  todayYmd: string;
  offReason?: string | null;
}): DayTimetable {
  const weekday = weekdayIndexYmd(input.date);
  const label = weekdayLabel(weekday);
  const schoolDay = input.schoolDay && weekday >= 1 && weekday <= 5;
  const dayLessons = schoolDay ? lessonsForWeekday(input.lessons, input.bells, weekday) : [];
  const until = dayLessons.length > 0 ? dayLessons[dayLessons.length - 1].end : null;
  const adjacent = addYmd(input.todayYmd, 1) === input.date;
  const packLabel = adjacent ? `Завтра, ${label}` : capitalizeUk(label);
  return {
    date: input.date,
    weekday,
    weekdayLabel: label,
    packLabel,
    schoolDay,
    offReason: schoolDay ? null : (input.offReason ?? null),
    until,
    lessons: dayLessons,
  };
}

export function describeNow(now: LessonNow): string | null {
  if (now.kind === "before") return `${now.slotOrder} урок · ${now.title} · з ${now.start}`;
  if (now.kind === "lesson") return `Зараз ${now.slotOrder} · ${now.title} · до ${now.end}`;
  if (now.kind === "break") return `Далі ${now.slotOrder} · ${now.title} · з ${now.start}`;
  if (now.kind === "done") return "Уже кінець";
  return null;
}
